/**
 * The optional backend probe.
 *
 * The app is designed to work with no server at all, so the single job of this
 * module is to answer "is the OAuth backend there, and is it configured?" If the
 * answer is no — no server, wrong URL, offline, or configured-but-broken — the
 * UI simply does not show the Connect button. A dead button that fails on click
 * is worse than no button, and that is exactly what the previous version did.
 */

export interface BackendConfig {
  /** True only when a server responded *and* reported working OAuth. */
  oauthEnabled: boolean;
  /** Where to send the browser to begin sign-in. */
  authUrl: string | null;
}

const OFFLINE: BackendConfig = { oauthEnabled: false, authUrl: null };

/** Base URL of the optional API. Empty string means "same origin". */
export function apiBaseUrl(): string {
  return (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '');
}

/**
 * Asks the backend whether OAuth is available.
 *
 * Never rejects and never blocks the UI for long: a 2.5-second timeout means a
 * dead or sleeping free-tier server costs a brief spinner, not a hung page.
 */
export async function fetchBackendConfig(signal?: AbortSignal): Promise<BackendConfig> {
  const timeout = AbortSignal.timeout(2500);
  const combined = signal ? AbortSignal.any([signal, timeout]) : timeout;

  try {
    const response = await fetch(`${apiBaseUrl()}/api/config`, {
      signal: combined,
      headers: { accept: 'application/json' },
    });
    if (!response.ok) return OFFLINE;

    const data: unknown = await response.json();
    if (typeof data !== 'object' || data === null) return OFFLINE;
    const record = data as Record<string, unknown>;
    if (record.oauthEnabled !== true) return OFFLINE;

    return {
      oauthEnabled: true,
      authUrl: `${apiBaseUrl()}/api/auth/linkedin`,
    };
  } catch {
    // No server, DNS failure, CORS rejection, timeout — all mean the same thing
    // to the UI, so they collapse to one answer.
    return OFFLINE;
  }
}
